/**
 * The "looks like markdown" heuristic from `editor.md` §8, weights and all.
 *
 * Signals are counted over the whole paste and compared against a threshold
 * that scales with length: `min(3, floor(lineCount / 5))`, markdown when the
 * count is **strictly greater**. A one-line paste therefore needs one signal;
 * anything 15 lines or longer needs four.
 */
export interface MarkdownSignals {
  score: number;
  threshold: number;
  isMarkdown: boolean;
}

function countMatches(text: string, pattern: RegExp): number {
  return text.match(pattern)?.length ?? 0;
}

export function markdownSignals(text: string): MarkdownSignals {
  const lines = text.split("\n");
  let score = 0;

  const fences = countMatches(text, /^```/gm);
  if (fences >= 2) score += fences;

  // Inline math: delimiters hugging their content, closing `$` not followed by
  // a digit — the rule that keeps "$5 and $6" out of math.
  score += countMatches(text, /\$[^\s$][^$]*[^\s$]\$(?!\d)|\$[^\s$]\$(?!\d)/g);

  score += 2 * countMatches(text, /\[[^\]]*]\(https?:\/\/[^)]+\)/g);
  score += 2 * countMatches(text, /\[[^\]]*]\(\/[^)]*\)/g);
  score += countMatches(text, /^#{1,6} \S/gm);
  score += countMatches(text, /^\s*[-*] \S/gm);
  // Lookahead, not a literal closing pipe: neighbouring alignment cells share
  // the pipe between them and both count.
  score += countMatches(text, /\|\s*:?-{2,}:?\s*(?=\|)/g);

  const threshold = Math.min(3, Math.floor(lines.length / 5));
  return { score, threshold, isMarkdown: score > threshold };
}

export function looksLikeMarkdown(text: string): boolean {
  return markdownSignals(text).isMarkdown;
}

/** §8.3 — a single URL, which is what may become a link, embed or mention. */
export function isSingleUrl(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === "" || /[\n\r]/.test(trimmed)) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** §8 "Multiple URLs": every line is a URL, and there is more than one. */
export function urlList(text: string): string[] | null {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  if (lines.length < 2) return null;
  return lines.every((line) => isSingleUrl(line)) ? lines : null;
}
